import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import useFetch from 'use-http';
import VoucherToPay from 'src/models/AP/VoucherToPay';

interface IChooseVouchersDialogProps {
    dialogName: string;
    handleSelectedVouchers?: (vouchers: VoucherToPay[]) => void;
    hideDialog: () => void;
}

const NO_EXPENSE_CODE_COLOR = '#FFA94D';
const NO_RECEIPTS_COLOR = '#FFB6C1';
const NO_CLIENT_PROSPECT_COLOR = '#FF6B6B';

// Mirrors ap_voucher.fmt's "Prepare Multiple Checks" MultiCheck window (Ap_Check block): a grid of
// every voucher eligible for a check (closed, unpaid, not voided) with the same 3 warning conditions
// the Oracle form checks before it'll let a single voucher's check be written (WHEN-BUTTON-PRESSED
// [oid 136]) called out here per-row via row color instead of blocking the whole batch.
const ChooseVouchersDialog = (props: IChooseVouchersDialogProps) => {
    const { get, response } = useFetch();
    const [vouchersToPay, setVouchersToPay] = useState<Array<VoucherToPay>>(undefined);
    const [selectedVoucherNumbers, setSelectedVoucherNumbers] = useState<Array<string>>([]);
    const isOpen = useSelector((state: IRootState) => selectors.layout.isDialogShowing(state, props.dialogName));

    // This component never unmounts - Dialog's CSSTransition (unmountOnExit) only removes its own
    // children's DOM, not this instance - so a mount-only effect would only ever fetch once, at
    // whatever moment VouchersPage first loaded, and never again on subsequent opens. Refetch on
    // every isOpen transition instead so a voucher closed since the last open actually shows up.
    useEffect(() => {
        if (isOpen) {
            getVouchersToPay();
        }
    }, [isOpen]);

    const getVouchersToPay = async () => {
        const result = await get('/v1.0/AccountsPayable/vouchersToPay');
        if (response.ok && result) {
            setVouchersToPay(result);
        } else {
            console.log('Unable to get the Vouchers to Pay!!!');
        }
        setSelectedVoucherNumbers([]);
    };

    const formatCurrency = (value: number | null | undefined): string => {
        if (value === null || value === undefined) {
            return '';
        }
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    };

    const formatDate = (date: Date | string | null | undefined): string => {
        return !!date ? new Date(date).toLocaleDateString() : '';
    };

    const rowColor = (voucher: VoucherToPay): string | undefined => {
        if (voucher.hasNoExpenseCode) {
            return NO_EXPENSE_CODE_COLOR;
        }
        if (voucher.hasNoReceipts) {
            return NO_RECEIPTS_COLOR;
        }
        if (voucher.hasNoClientProspect) {
            return NO_CLIENT_PROSPECT_COLOR;
        }
        return undefined;
    };

    const isSelected = (voucher: VoucherToPay) => selectedVoucherNumbers.includes(voucher.voucherNumber);

    const toggleSelected = (voucher: VoucherToPay) => {
        setSelectedVoucherNumbers(isSelected(voucher)
            ? selectedVoucherNumbers.filter(v => v !== voucher.voucherNumber)
            : [...selectedVoucherNumbers, voucher.voucherNumber]);
    };

    const selectAll = () => {
        setSelectedVoucherNumbers((vouchersToPay || []).map(v => v.voucherNumber));
    };

    const deselectAll = () => {
        setSelectedVoucherNumbers([]);
    };

    const amountSelected = (vouchersToPay || [])
        .filter(isSelected)
        .reduce((total, v) => total + (v.amount || 0), 0);

    const paySelected = () => {
        const selectedVouchers = (vouchersToPay || []).filter(isSelected);
        if (selectedVouchers.length === 0) {
            return;
        }
        // No props.hideDialog() here - handleSelectedVouchers() (in VouchersPage) calls
        // showDialog(ENTER_CHECK_NUMBER) to move to the next dialog in the chain, and activeDialog
        // is a single Redux field. Calling hideDialog() afterward would run last and clobber that
        // back to undefined instead of leaving Enter Check Number showing.
        props.handleSelectedVouchers(selectedVouchers);
    };

    return (
        <Dialog title="Prepare Multiple Checks" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '65%' }}>
            <div style={{ overflowY: 'scroll', height: '20rem' }}>
                <table style={{ width: '100%' }}>
                    <caption
                        style={{
                            captionSide: 'top', textAlign: 'left', padding: '.5rem 0', fontSize: '.9rem'
                        }}
                    >
                        <span
                            style={{
                                display: 'inline-block', width: '28px', height: '14px', margin: '0 5px 0 0',
                                border: '1px solid black', verticalAlign: 'middle', backgroundColor: NO_EXPENSE_CODE_COLOR
                            }}
                        />
                        No Expense Code Defined
                        <span
                            style={{
                                display: 'inline-block', width: '28px', height: '14px', margin: '0 5px 0 1rem',
                                border: '1px solid black', verticalAlign: 'middle', backgroundColor: NO_RECEIPTS_COLOR
                            }}
                        />
                        No Receipts Attached
                        <span
                            style={{
                                display: 'inline-block', width: '28px', height: '14px', margin: '0 5px 0 1rem',
                                border: '1px solid black', verticalAlign: 'middle', backgroundColor: NO_CLIENT_PROSPECT_COLOR
                            }}
                        />
                        No Client/Prospect Defined
                    </caption>
                    <thead style={{ textAlign: 'center' }}>
                        <tr>
                            <th style={{ width: '4%' }}/>
                            <th className="find-person-header" style={{ width: '9%' }}>Due Date</th>
                            <th className="find-person-header" style={{ width: '6%' }}>Vndr No.</th>
                            <th className="find-person-header" style={{ width: '13%' }}>Vendor</th>
                            <th className="find-person-header" style={{ width: '7%' }}>Voucher</th>
                            <th className="find-person-header" style={{ width: '9%' }}>Inv. Date</th>
                            <th className="find-person-header" style={{ width: '8%', textAlign: 'right' }}>Amount</th>
                            <th className="find-person-header" style={{ width: '7%' }}>Chk No.</th>
                            <th className="find-person-header" style={{ width: '9%' }}>Check Date</th>
                            <th className="find-person-header" style={{ width: '18%' }}>Description</th>
                        </tr>
                    </thead>
                    {vouchersToPay?.map((voucher: VoucherToPay) => {
                        return (
                            <tbody key={voucher.voucherNumber}>
                                <tr
                                    className="table-item-hover"
                                    style={{ textAlign: 'left', backgroundColor: rowColor(voucher) }}
                                    onClick={() => toggleSelected(voucher)}
                                >
                                    <td style={{ width: '4%', textAlign: 'center' }}>
                                        <input type="checkbox" checked={isSelected(voucher)} onChange={() => toggleSelected(voucher)} onClick={(e) => e.stopPropagation()}/>
                                    </td>
                                    <td style={{ width: '9%' }}>{formatDate(voucher.dueDate)}</td>
                                    <td style={{ width: '6%' }}>{voucher.vendorNumber}</td>
                                    <td style={{ width: '13%' }}>{voucher.vendorName}</td>
                                    <td style={{ width: '7%' }}>{voucher.voucherNumber}</td>
                                    <td style={{ width: '9%' }}>{formatDate(voucher.invoiceDate)}</td>
                                    <td style={{ width: '8%', textAlign: 'right' }}>{formatCurrency(voucher.amount)}</td>
                                    <td style={{ width: '7%' }}>{voucher.checkNumber ?? ''}</td>
                                    <td style={{ width: '9%' }}>{formatDate(voucher.checkDate)}</td>
                                    <td style={{ width: '18%' }}>{voucher.description}</td>
                                </tr>
                            </tbody>
                        );
                    })}
                </table>
                {vouchersToPay && vouchersToPay.length === 0 &&
                    <div style={{ textAlign: 'center', fontSize: '.9rem', margin: '1rem 0' }}>No Vouchers Available to Pay</div>
                }
            </div>
            <div className="form-row" style={{ marginTop: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="form-row">
                    <Button
                        className="blue-hover-button"
                        style={{ width: '10rem', marginRight: '1rem' }}
                        text={'Select All'}
                        onClick={() => selectAll()}
                    />
                    <Button
                        className="blue-hover-button"
                        style={{ width: '10rem', marginRight: '1rem' }}
                        text={'Deselect All'}
                        onClick={() => deselectAll()}
                    />
                    <Button
                        className="blue-hover-button"
                        style={{ width: '10rem' }}
                        text={'Pay Selected'}
                        disabled={selectedVoucherNumbers.length === 0}
                        onClick={() => paySelected()}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ marginRight: '.5rem', fontWeight: 'bold' }}>Amount Selected</label>
                    <label style={{ backgroundColor: '#66FF00', padding: '.25rem .75rem', fontWeight: 'bold', border: '1px solid black' }}>
                        {formatCurrency(amountSelected)}
                    </label>
                </div>
            </div>
            <div className="form-row" style={{ marginTop: '1rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '12%' }}
                    text={'Cancel'}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default ChooseVouchersDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/ChooseVouchersDialog.tsx